import {
  type HhDataItem,
  SiteDemandView,
  useDemandData,
  usePreviewChangelog,
  useSiteUpdate,
} from "@inrange/building-manager-api-client";
import {
  getESysName,
  SiteChangeLog,
  SiteDemand,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Form, Modal, Table } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Select from "react-select";
import Loading from "../Loading";
import { ChangelogModal } from "../site/siteSections/ChangelogModal";
import { HhDataTempFile } from "./DemandFileSummaryRow";
import DemandFileSummaryTable from "./DemandFileSummaryTable";

export interface SiteDemandRow {
  siteId: string;
  siteName: string;
  demandIdx: string;
  esysIdx: string;
  esys: Pick<
    SiteElectricalSystem,
    "idx" | "demandIdx" | "importGridConnectionId"
  >;
  demand: Pick<SiteDemand, "buildingProfile" | "existingPvKwp">;
  displayLabel: string;
  orgs: { orgID: string; name?: string }[];
}

const DemandDataSiteAssignment = ({
  hhDataItem,
  siteMap,
  onClose,
}: {
  hhDataItem: HhDataItem;
  siteMap: Map<string, SiteDemandView>;
  onClose: () => void;
}) => {
  const { fetchHhDataMetadata } = useDemandData();
  const siteUpdate = useSiteUpdate();
  const previewChangelog = usePreviewChangelog();
  const queryClient = useQueryClient();

  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const [filterText, setFilterText] = useState<string>("");
  const [debouncedFilterText, setDebouncedFilterText] = useState<string>("");
  const [filteredOrgIds, setFilteredOrgIds] = useState<string[]>([]);
  const [scenarioSiteId, setScenarioSiteId] = useState<string | null>(null);
  const [onlyShowSelected, setOnlyShowSelected] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<HhDataTempFile | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);

  const [alertStyle, setAlertStyle] = useState<string>("light");
  const [alertMessage, setAlertMessage] = useState<
    ReactNode | string | undefined
  >(undefined);

  // Changelog preview state
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [changelogPreview, setChangelogPreview] =
    useState<SiteChangeLog | null>(null);
  const [changelogSiteId, setChangelogSiteId] = useState<string | null>(null);

  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

  // Fetch metadata on mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const response = await fetchHhDataMetadata.mutateAsync(hhDataItem.guid);
        // Map the API response to HhDataTempFile structure
        setMetadata({
          fileGuid: hhDataItem.guid,
          fileName: hhDataItem.fileName || hhDataItem.guid,
          uploadedAt: hhDataItem.processedTime || 0,
          outputMetaData: response.metadata,
        });
      } catch (error) {
        console.error("Error fetching metadata:", error);
      } finally {
        setMetadataLoading(false);
      }
    };
    loadMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hhDataItem.guid]);

  // Debounce filter text updates
  useEffect(() => {
    const debounceWaitMillis = testMode ? 0 : 300;
    const timeoutId = setTimeout(() => {
      setDebouncedFilterText(filterText);
    }, debounceWaitMillis);
    return () => clearTimeout(timeoutId);
  }, [filterText, testMode]);

  // Transform sites into flat (Site, Demand) rows
  const allRows: SiteDemandRow[] = useMemo(() => {
    const rows: SiteDemandRow[] = [];
    for (const site of siteMap.values()) {
      const esysCount = Object.keys(site.esys).length;
      const demandCount = Object.keys(site.demand).length;

      for (const [esysIdx, esys] of Object.entries(site.esys)) {
        const demandIdx = String(esys.demandIdx);
        const demand = site.demand[demandIdx];
        if (!demand) continue;

        const esysName = getESysName(esysCount, esys);
        const displayLabel =
          demandCount === 1
            ? site.name
            : `${site.name} -> ${esysName.trim() || `Demand ${parseInt(demandIdx) + 1}`}`;

        rows.push({
          siteId: site.id,
          siteName: site.name,
          demandIdx,
          esysIdx,
          esys,
          demand,
          displayLabel,
          orgs: site.siteOwnerships.map((o) => ({
            orgID: o.orgID,
            name: o.name,
          })),
        });
      }
    }

    return rows.sort((a, b) =>
      a.displayLabel.toLowerCase().localeCompare(b.displayLabel.toLowerCase())
    );
  }, [siteMap]);

  // Build list of unique organisations for filtering
  const filterOrgs = useMemo(() => {
    const orgMap = new Map<string, string>();
    for (const row of allRows) {
      for (const org of row.orgs) {
        if (org.name && !orgMap.has(org.orgID)) {
          orgMap.set(org.orgID, org.name);
        }
      }
    }
    return Array.from(orgMap.entries())
      .map(([orgId, name]) => ({ orgId, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allRows]);

  // Build list of sites for scenario filtering (sorted by name)
  const scenarioSiteOptions = useMemo(() => {
    return Array.from(siteMap.values())
      .map((site) => ({ siteId: site.id, name: site.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [siteMap]);

  // Get the set of site IDs to show when scenario filter is active
  const scenarioSiteIds = useMemo(() => {
    if (!scenarioSiteId || !siteMap) return null;
    const site = Array.from(siteMap.values()).find(
      (s) => s.id === scenarioSiteId
    );
    if (!site) return null;
    const ids = new Set<string>([site.id, ...(site.nearbySiteIDs || [])]);
    return ids;
  }, [scenarioSiteId, siteMap]);

  const findPreSelectedVisibleRowIds = useCallback(
    (currentlyFilteredRows: SiteDemandRow[]): Set<string> => {
      const visibleRowIds = new Set<string>();
      for (const row of currentlyFilteredRows) {
        // Search within hhDataItem.siteIds for a matching mapping
        const isMapped = hhDataItem.siteMappings.some(
          (mapping) =>
            mapping.site.id === row.siteId &&
            String(mapping.demandIdx) === row.demandIdx
        );
        if (isMapped) {
          const rowId = `${row.siteId}:${row.demandIdx}:${row.esysIdx}`;
          visibleRowIds.add(rowId);
        }
      }
      return visibleRowIds;
    },
    [hhDataItem.siteMappings]
  );

  // Filter rows based on search text, org filter, scenario filter, and selected-only filter
  const filteredRows = useMemo(() => {
    const filterWords = debouncedFilterText.toLowerCase().split(" ");

    return allRows.filter((row) => {
      const rowId = `${row.siteId}:${row.demandIdx}:${row.esysIdx}`;

      // Filter by selected only
      if (onlyShowSelected && !selectedRowIds.has(rowId)) {
        return false;
      }

      // Filter by scenario site
      if (scenarioSiteIds && !scenarioSiteIds.has(row.siteId)) {
        return false;
      }

      // Filter by search text
      if (debouncedFilterText) {
        const searchText = `${row.displayLabel.toLowerCase()}${row.siteId}`;
        if (!filterWords.every((word) => searchText.includes(word))) {
          return false;
        }
      }

      // Filter by organisation
      if (filteredOrgIds.length > 0) {
        const rowOrgIds = row.orgs.map((o) => o.orgID);
        if (!filteredOrgIds.some((orgId) => rowOrgIds.includes(orgId))) {
          return false;
        }
      }

      return true;
    });
  }, [
    debouncedFilterText,
    allRows,
    onlyShowSelected,
    selectedRowIds,
    scenarioSiteIds,
    filteredOrgIds,
  ]);

  // Initialize selection from existing mappings once allRows is available
  useEffect(() => {
    if (
      !hasInitializedSelection &&
      allRows.length > 0 &&
      hhDataItem.siteMappings.length > 0
    ) {
      setSelectedRowIds(findPreSelectedVisibleRowIds(filteredRows));
      setHasInitializedSelection(true);
    }
  }, [
    allRows,
    hhDataItem.siteMappings,
    hasInitializedSelection,
    findPreSelectedVisibleRowIds,
    filteredRows,
    hhDataItem.siteMappings.length,
  ]);

  const onSetSelected = useCallback((rowId: string, selected: boolean) => {
    setSelectedRowIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });
  }, []);

  const getRowId = (row: SiteDemandRow) =>
    `${row.siteId}:${row.demandIdx}:${row.esysIdx}`;

  const prepareDemandPayload = (siteId: string, demandIdx: string) => {
    const demandPayload: {
      [key: string]: {
        hhDemandGuid?: string;
        hhDemandFileDescription?: string;
      };
    } = {};
    demandPayload[demandIdx] = {
      hhDemandGuid: hhDataItem.guid,
      hhDemandFileDescription: `${metadata?.outputMetaData?.input_file} (Processed on: ${metadata?.outputMetaData?.date_of_analysis})`,
    };
    return {
      site: {
        id: siteId,
        demand: demandPayload,
      },
    };
  };

  const handlePreviewChangelog = async (row: SiteDemandRow) => {
    const update = prepareDemandPayload(row.siteId, row.demandIdx);

    try {
      const response = await previewChangelog.mutateAsync({
        siteId: row.siteId,
        update,
      });
      if (response.log) {
        setChangelogPreview(response.log);
      } else {
        setChangelogPreview({
          siteID: row.siteId,
          time: Math.floor(Date.now() / 1000),
          sortTime: Math.floor(Date.now() / 1000),
          updatedBy: "",
          userChange: {},
          recalculated: {},
        });
      }
      setChangelogSiteId(row.siteId);
      setShowChangelogModal(true);
    } catch (error: any) {
      console.error("Error previewing changelog:", error);
      setAlertStyle("danger");
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to preview changelog";
      setAlertMessage(errorMessage);
    }
  };

  const handleAssignDemand = async () => {
    const selectedRows = filteredRows.filter((row) =>
      selectedRowIds.has(getRowId(row))
    );

    if (selectedRows.length === 0) return;

    setAlertStyle("light");
    setAlertMessage(`Updating ${selectedRows.length} site(s)...`);

    // Group updates by siteId since we may have multiple demands per site
    const updatesBySite: {
      [siteId: string]: { demandUpdates: { demandIdx: string }[] };
    } = {};

    for (const row of selectedRows) {
      if (!updatesBySite[row.siteId]) {
        updatesBySite[row.siteId] = { demandUpdates: [] };
      }
      updatesBySite[row.siteId].demandUpdates.push({
        demandIdx: row.demandIdx,
      });
    }

    const mutations = Object.entries(updatesBySite).map(
      ([siteId, { demandUpdates }]) => {
        // Build the demand update payload
        const demandPayload: {
          [key: string]: {
            hhDemandGuid?: string;
            hhDemandFileDescription?: string;
          };
        } = {};
        for (const { demandIdx } of demandUpdates) {
          demandPayload[demandIdx] = {
            hhDemandGuid: hhDataItem.guid,
            hhDemandFileDescription: `${metadata?.outputMetaData?.input_file} (Processed on: ${metadata?.outputMetaData?.date_of_analysis})`,
          };
        }

        return siteUpdate.mutateAsync({
          siteId,
          payload: {
            site: {
              id: siteId,
              demand: demandPayload,
            },
          },
        });
      }
    );

    try {
      const results = await Promise.allSettled(mutations);
      queryClient.invalidateQueries(["site"]);
      queryClient.invalidateQueries(["sites"]);
      queryClient.invalidateQueries(["hh-data"]);

      for (const siteId of Object.keys(updatesBySite)) {
        queryClient.invalidateQueries(["site", siteId]);
      }

      const successfulUpdates = results.filter(
        (result) => result.status === "fulfilled"
      ).length;
      const failedUpdates = results.filter(
        (result) => result.status === "rejected"
      ).length;

      const failedSiteDetails = results
        .filter((result) => result.status === "rejected")
        .map((result, idx) => {
          const siteId = Object.keys(updatesBySite)[idx];
          const errorMessage =
            (result as PromiseRejectedResult).reason?.response?.data?.message ||
            (result as PromiseRejectedResult).reason?.message ||
            (result as PromiseRejectedResult).reason?.toString() ||
            "Unknown error";
          const siteName =
            allRows.find((r) => r.siteId === siteId)?.siteName || siteId;
          return { siteId, siteName, errorMessage };
        });

      if (failedUpdates === 0) {
        setAlertStyle("success");
        setAlertMessage(
          `Successfully assigned demand data to ${successfulUpdates} site(s)`
        );
        setSelectedRowIds(new Set());
      } else {
        setAlertStyle("danger");
        setAlertMessage(
          <>
            <div>
              Failed to update {failedUpdates} out of{" "}
              {Object.keys(updatesBySite).length} site(s)
            </div>
            <ul style={{ margin: 0 }}>
              {failedSiteDetails
                .slice(0, 10)
                .map(({ siteId, siteName, errorMessage }) => (
                  <li key={siteId}>
                    <Link to={`/site/${siteId}/edit`}>{siteName}</Link> -{" "}
                    {errorMessage}
                  </li>
                ))}
              {failedSiteDetails.length > 10 && (
                <li>{`and ${failedSiteDetails.length - 10} more...`}</li>
              )}
            </ul>
          </>
        );
      }
    } catch (error) {
      setAlertStyle("danger");
      setAlertMessage(`An unexpected error occurred while updating sites`);
    }
  };

  return (
    <>
      <Modal
        show={true}
        onHide={onClose}
        size="xl"
        style={{ marginTop: "5vh" }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Assign Demand Data to Sites
            <div
              style={{ fontSize: "14px", fontWeight: "normal", color: "#666" }}
            >
              File: {hhDataItem.fileName || hhDataItem.guid}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "80vh", overflow: "auto" }}>
          {/* Metadata Card */}
          {metadataLoading ? (
            <Card className="mb-3">
              <Card.Body>
                <Loading label="Loading file metadata..." />
              </Card.Body>
            </Card>
          ) : metadata ? (
            <DemandFileSummaryTable files={[metadata]} showActions={false} />
          ) : null}

          {alertMessage && (
            <Alert
              variant={alertStyle}
              onClose={() => setAlertMessage(undefined)}
              dismissible={alertStyle !== "light"}
            >
              <div>{alertMessage}</div>
            </Alert>
          )}

          <div
            style={{
              display: "flex",
              gap: "16px",
              marginBottom: "16px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Form.Control
              type="search"
              placeholder="Search sites..."
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
              }}
              style={{ width: "300px" }}
            />
            <Select
              placeholder="Filter by organisation..."
              onChange={(values) => {
                setFilteredOrgIds(
                  values ? values.map((v: { value: string }) => v.value) : []
                );
              }}
              options={filterOrgs.map((org) => ({
                label: org.name,
                value: org.orgId,
              }))}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  border: "var(--bs-border-width) solid var(--bs-border-color)",
                  borderRadius: "var(--bs-border-radius)",
                  color: "var(--bs-body-color)",
                  minWidth: "250px",
                }),
              }}
              isMulti
            />
            <Select
              placeholder="Filter by scenarios of site..."
              onChange={(value: { value: string } | null) => {
                setScenarioSiteId(value ? value.value : null);
              }}
              options={scenarioSiteOptions.map((site) => ({
                label: site.name,
                value: site.siteId,
              }))}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  border: "var(--bs-border-width) solid var(--bs-border-color)",
                  borderRadius: "var(--bs-border-radius)",
                  color: "var(--bs-body-color)",
                  minWidth: "250px",
                }),
              }}
              isClearable
            />
            <div style={{ marginLeft: "auto" }}>
              <Button
                variant="success"
                onClick={handleAssignDemand}
                disabled={selectedRowIds.size === 0}
              >
                Assign demand to {selectedRowIds.size} site(s)
              </Button>
            </div>
          </div>

          <div
            style={{
              fontSize: "14px",
              marginBottom: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              Showing {filteredRows.length} of {allRows.length} site demand
              mappings
              {selectedRowIds.size > 0 && ` (${selectedRowIds.size} selected)`}
            </span>
            <Form.Check
              type="checkbox"
              id="only-show-selected"
              label="Only show selected"
              checked={onlyShowSelected}
              onChange={(e) => setOnlyShowSelected(e.target.checked)}
              disabled={selectedRowIds.size === 0}
            />
          </div>

          <Table striped bordered size="sm" responsive>
            <thead>
              <tr>
                <th style={{ width: "40px" }}>
                  <Form.Check
                    type="checkbox"
                    checked={
                      filteredRows.length > 0 &&
                      selectedRowIds.size === filteredRows.length
                    }
                    onChange={() => {
                      if (selectedRowIds.size !== filteredRows.length) {
                        setSelectedRowIds(
                          new Set(filteredRows.map((row) => getRowId(row)))
                        );
                      } else {
                        setSelectedRowIds(new Set());
                      }
                    }}
                  />
                </th>
                <th>Site / Demand</th>
                <th>Organisations</th>
                <th style={{ width: "140px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const rowId = getRowId(row);
                const isSelected = selectedRowIds.has(rowId);
                return (
                  <SiteDemandAssignmentRow
                    key={rowId}
                    row={row}
                    isSelected={isSelected}
                    onSetSelected={onSetSelected}
                    rowId={rowId}
                    onPreviewChangelog={handlePreviewChangelog}
                    isPreviewLoading={previewChangelog.isLoading}
                  />
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {showChangelogModal && changelogPreview && changelogSiteId && (
        <ChangelogModal
          logItem={changelogPreview}
          siteId={changelogSiteId}
          lossFactorsByKey={{}}
          onClose={() => setShowChangelogModal(false)}
          zIndex={1060}
        />
      )}
    </>
  );
};

interface SiteDemandAssignmentRowProps {
  row: SiteDemandRow;
  isSelected: boolean;
  onSetSelected: (rowId: string, selected: boolean) => void;
  rowId: string;
  onPreviewChangelog: (row: SiteDemandRow) => void;
  isPreviewLoading: boolean;
}

const SiteDemandAssignmentRow = ({
  row,
  isSelected,
  onSetSelected,
  rowId,
  onPreviewChangelog,
  isPreviewLoading,
}: SiteDemandAssignmentRowProps) => {
  return (
    <tr>
      <td>
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSetSelected(rowId, e.target.checked)}
        />
      </td>
      <td>
        <Link to={`/site/${row.siteId}/edit`}>{row.displayLabel}</Link>
        {row.demand.existingPvKwp && (
          <Alert variant="warning" className="mt-2 mb-0 p-2">
            NOTE: {row.demand.existingPvKwp} kWp existing solar <br /> Remove
            this if demand data already accounts for this
          </Alert>
        )}
      </td>
      <td>
        {row.orgs
          .filter((o) => o.name)
          .map((o) => o.name)
          .join(", ") || "—"}
      </td>
      <td>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => onPreviewChangelog(row)}
          disabled={isPreviewLoading}
        >
          Preview changelog
        </Button>
      </td>
    </tr>
  );
};

export default DemandDataSiteAssignment;
